import { z } from 'zod';
import { Audit, ComplianceCase, EnforcementAction, Inspection, MonitoringRule, Obligation } from './compliance.js';
import { DeviceClassRule, DeviceModel, DeviceUnit, Installation } from './devices.js';
import { Incident } from './incidents.js';
import { Application, Participant } from './participants.js';
import { AffectedUnit, Recall } from './recalls.js';
// ---- Data lake export (docs/lake-export.md) ----------------------------------
export const LAKE_SCHEMA_VERSION = 'v1';
/** Entity types exported to the lake; each is one folder in the container. */
export const LakeEntityType = z.enum([
    'device_model',
    'device_unit',
    'installation',
    'participant',
    'application',
    'incident',
    'recall',
    'recall_unit',
    'compliance_case',
    'compliance_inspection',
    'compliance_audit',
    'compliance_enforcement_action',
    'obligation',
    'device_class_rule',
    'monitoring_rule',
]);
/**
 * The API contract each entity type's snapshot (`entity`) is parsed through.
 * `recordBusinessEvent` refuses a snapshot whose fields don't match it, and the
 * data dictionary (docs/lake-data-dictionary.md) documents each entry from the
 * JSDoc on the contract and on the entry here.
 */
export const lakeEntityContracts = {
    /** A device model registered by a manufacturer, with its approval status and class-specific attributes. @metadata business */
    device_model: DeviceModel,
    /** A physical device unit of a registered model, with its owner, lifecycle and compliance status. @metadata business */
    device_unit: DeviceUnit,
    /** The installation of a unit at a connection point (NMI) and site address. @metadata business */
    installation: Installation,
    /** An organisation registered in the scheme (manufacturer, importer, installer, retailer or aggregator). @metadata business */
    participant: Participant,
    /** An organisation's application to become a participant, with the participant record it is for and its assessment notes. @metadata business */
    application: Application,
    /** A fault report against a model, optionally pinned to a unit and installation, with its event trail. @metadata business */
    incident: Incident,
    /** A recall raised by CER against a model, with its event trail and every affected unit. @metadata business */
    recall: Recall,
    /** One unit in scope of a recall: a snapshot of the unit when the recall resolved it, and its resolution trail. @metadata business */
    recall_unit: AffectedUnit,
    /** A compliance case about a participant (and optionally a unit), with its linked signals, inspections, audits, enforcement actions and event trail. @metadata business */
    compliance_case: ComplianceCase,
    /** An on-site inspection within a compliance case, with its findings and outcome. @metadata business */
    compliance_inspection: Inspection,
    /** A compliance audit of a participant over a period, with its findings and follow-up actions. @metadata business */
    compliance_audit: Audit,
    /** An enforcement action against a participant within a compliance case, with the obligation and evidence relied on. @metadata business */
    compliance_enforcement_action: EnforcementAction,
    /** One version of a regulatory obligation that findings and enforcement actions rely on. @metadata business */
    obligation: Obligation,
    /** One effective-dated version of a device class's rule set: the JSON Schemas and duplicate keys that models, units and installations of the class must satisfy. @metadata business */
    device_class_rule: DeviceClassRule,
    /** One version of a monitoring rule: a predicate over register records whose hits become risk signals. @metadata business */
    monitoring_rule: MonitoringRule,
};
const contractKeys = new Map();
/** Why a snapshot doesn't match its entity type's contract (fields it shouldn't have, or required ones it lacks), or null if it does. */
export function lakeContractMismatch(entityType, entity) {
    let keys = contractKeys.get(entityType);
    if (!keys) {
        const shape = lakeEntityContracts[entityType].shape;
        keys = { all: new Set(Object.keys(shape)), required: Object.keys(shape).filter((k) => !shape[k].isOptional()) };
        contractKeys.set(entityType, keys);
    }
    const unexpected = Object.keys(entity).filter((k) => !keys.all.has(k));
    const missing = keys.required.filter((k) => !(k in entity));
    if (unexpected.length === 0 && missing.length === 0)
        return null;
    return `${entityType} snapshot doesn't match its lake contract (unexpected: ${unexpected.join(', ') || 'none'}; missing: ${missing.join(', ') || 'none'})`;
}
export const lakeEventCatalogue = {
    'device_model.register': { eventType: 'device_model.registered', entityType: 'device_model' },
    'device_model.approved': { eventType: 'device_model.approved', entityType: 'device_model' },
    'device_model.withdrawn': { eventType: 'device_model.withdrawn', entityType: 'device_model' },
    'device_model.correct': { eventType: 'device_model.corrected', entityType: 'device_model' },
    'device_unit.register': { eventType: 'device_unit.registered', entityType: 'device_unit' },
    'device_unit.decommissioned': { eventType: 'device_unit.decommissioned', entityType: 'device_unit' },
    'device_unit.withdrawn': { eventType: 'device_unit.withdrawn', entityType: 'device_unit' },
    'device_unit.transfer': { eventType: 'device_unit.transferred', entityType: 'device_unit' },
    'device_unit.compliance_status': { eventType: 'device_unit.compliance_status_changed', entityType: 'device_unit' },
    'device_unit.correct': { eventType: 'device_unit.corrected', entityType: 'device_unit' },
    'installation.register': { eventType: 'installation.recorded', entityType: 'installation' },
    'installation.decommission': { eventType: 'installation.decommissioned', entityType: 'installation' },
    'installation.correct': { eventType: 'installation.corrected', entityType: 'installation' },
    'participant.self_update': { eventType: 'participant.updated', entityType: 'participant' },
    'participant.update': { eventType: 'participant.updated', entityType: 'participant' },
    'participant.correct': { eventType: 'participant.corrected', entityType: 'participant' },
    'participant.approved': { eventType: 'participant.approved', entityType: 'participant' },
    'participant.suspended': { eventType: 'participant.suspended', entityType: 'participant' },
    'application.submit': { eventType: 'application.submitted', entityType: 'application' },
    'application.assess': { eventType: 'application.assessed', entityType: 'application' },
    'application.approve': { eventType: 'application.approved', entityType: 'application' },
    'application.reject': { eventType: 'application.rejected', entityType: 'application' },
    'application.return': { eventType: 'application.returned', entityType: 'application' },
    'incident.raise': { eventType: 'incident.raised', entityType: 'incident' },
    'incident.status': { eventType: 'incident.status_changed', entityType: 'incident' },
    'recall.raise': { eventType: 'recall.raised', entityType: 'recall' },
    'recall.refresh': { eventType: 'recall_unit.added', entityType: 'recall_unit', of: { entityType: 'recall', key: 'recallId' }, each: true },
    'recall.close': { eventType: 'recall.closed', entityType: 'recall' },
    // Audited against the recall; the event is the affected unit, the only part a responsible participant sees in full.
    'recall.unit.status': { eventType: 'recall_unit.status_changed', entityType: 'recall_unit', of: { entityType: 'recall', key: 'recallId' } },
    'compliance.case.open': { eventType: 'compliance_case.opened', entityType: 'compliance_case' },
    'compliance.case.reassign': { eventType: 'compliance_case.owner_reassigned', entityType: 'compliance_case' },
    'compliance.case.close': { eventType: 'compliance_case.closed', entityType: 'compliance_case' },
    'compliance.inspection.schedule': { eventType: 'compliance_inspection.scheduled', entityType: 'compliance_inspection' },
    'compliance.inspection.complete': { eventType: 'compliance_inspection.completed', entityType: 'compliance_inspection' },
    'compliance.inspection.cancel': { eventType: 'compliance_inspection.cancelled', entityType: 'compliance_inspection' },
    'compliance.audit.plan': { eventType: 'compliance_audit.planned', entityType: 'compliance_audit' },
    'compliance.audit.conduct': { eventType: 'compliance_audit.conducted', entityType: 'compliance_audit' },
    'compliance.audit.close': { eventType: 'compliance_audit.closed', entityType: 'compliance_audit' },
    'compliance.audit.cancel': { eventType: 'compliance_audit.cancelled', entityType: 'compliance_audit' },
    'compliance.enforcement_action.draft': { eventType: 'compliance_enforcement_action.drafted', entityType: 'compliance_enforcement_action' },
    'compliance.enforcement_action.issue': { eventType: 'compliance_enforcement_action.issued', entityType: 'compliance_enforcement_action' },
    'compliance.enforcement_action.withdraw': { eventType: 'compliance_enforcement_action.withdrawn', entityType: 'compliance_enforcement_action' },
    'compliance.enforcement_action.close': { eventType: 'compliance_enforcement_action.closed', entityType: 'compliance_enforcement_action' },
    'compliance.obligation.publish': { eventType: 'obligation.published', entityType: 'obligation' },
    'compliance.monitoring_rule.publish': { eventType: 'monitoring_rule.published', entityType: 'monitoring_rule' },
    'rules.publish': { eventType: 'device_class_rule.published', entityType: 'device_class_rule' },
    'sim.base_load.batch': { eventType: 'installation.recorded', entityType: 'installation', of: { entityType: 'sim_batch' }, each: true },
    'installation.import_legacy': { eventType: 'installation.recorded', entityType: 'installation', of: { entityType: 'sim_batch' }, each: true },
};
/**
 * Events for another register record changed by the same transaction, written
 * after the action's own event and linked to the same audit event.
 */
export const lakeRelatedEvents = {
    'device_unit.registered': 'device_unit',
    'device_unit.installed': 'device_unit',
    'device_unit.uninstalled': 'device_unit',
    'device_model.withdrawn': 'device_model',
    'incident.recalled': 'incident',
    'participant.status_changed': 'participant',
    'device_class_rule.superseded': 'device_class_rule',
    'monitoring_rule.superseded': 'monitoring_rule',
    'obligation.superseded': 'obligation',
};
/** A correction's new history version: which version it corrects and the facts it records. */
/** The corrected facts carried by a correction event. */
export const LakeCorrection = z.object({
    /** History id of the new version holding the corrected facts. @metadata technical @source history.device_versions.id @source history.participant_versions.id @transform bigint to number */
    versionId: z.number().int(),
    /** History id of the version whose facts were wrong. @metadata technical @source history.device_versions.correction_of @source history.participant_versions.correction_of @transform bigint to number */
    correctionOf: z.number().int(),
    /** When the corrected facts were actually true. @metadata business @source history.device_versions.valid_from @source history.participant_versions.valid_from @transform timestamptz as an ISO 8601 UTC string */
    validFrom: z.string(),
    /** The corrected facts. @metadata business @source history.device_versions.data @source history.participant_versions.data */
    data: z.record(z.unknown()),
});
/**
 * One line of an exported JSON Lines file: a business event written to `lake.outbox`
 * in the same transaction as the change and its audit event.
 * @metadata technical
 */
export const BusinessEvent = z.object({
    /** Unique id of the event; consumers use it to de-duplicate re-uploaded batches. @metadata technical @source lake.outbox.event_id @transform generated by the database (gen_random_uuid) */
    eventId: z.string().uuid(),
    /** Version of this envelope format. @metadata technical @source lake.outbox.schema_version @transform always LAKE_SCHEMA_VERSION */
    schemaVersion: z.literal(LAKE_SCHEMA_VERSION),
    /** The business transaction, e.g. device_unit.registered. @metadata business @source lake.outbox.event_type @transform looked up in lakeEventCatalogue from the audit action, or a related event type */
    eventType: z.string(),
    /** Type of record changed; one export folder per type. @metadata business @source lake.outbox.entity_type */
    entityType: LakeEntityType,
    /** Id of the record changed. @metadata technical @source lake.outbox.entity_id */
    entityId: z.string(),
    /** Version of the record after the change; null for records without a version. @metadata technical @source lake.outbox.entity_version @transform the snapshot's version field */
    entityVersion: z.number().int().nullable(),
    /** When the transaction recorded the event. @metadata technical @source lake.outbox.occurred_at @transform timestamptz as an ISO 8601 UTC string */
    occurredAt: z.string(),
    /** Participant the event belongs to (its tenant); null for CER-wide records. @metadata technical @source lake.outbox.participant_id */
    participantId: z.string().nullable(),
    /** User or service that made the change. @metadata technical @source lake.outbox.actor_id */
    actorId: z.string().nullable(),
    /** Kind of actor: user, service or anonymous. @metadata technical @source lake.outbox.actor_type */
    actorType: z.enum(['user', 'service', 'anonymous']),
    /** Id of the API request that made the change, shared with its audit event and logs. @metadata technical @source lake.outbox.correlation_id */
    correlationId: z.string().nullable(),
    /** The audit.events row recorded in the same transaction. @metadata technical @source lake.outbox.audit_event_id @transform bigint to number */
    auditEventId: z.number().int(),
    /** The record after the change, in its API contract shape (one of the entity sections below). @metadata business @source lake.outbox.entity @transform parsed through the entity type's contract (lakeEntityContracts) before it is written; jsonb */
    entity: z.record(z.unknown()),
    /** Set on corrections. Correcting an earlier version leaves entity unchanged, so the corrected facts are only here. @metadata business @source lake.outbox.correction @transform jsonb; null on other events */
    correction: LakeCorrection.nullable(),
});
/**
 * Export progress for one entity type (CER export status view). `claimed` rows
 * are in a batch whose upload hasn't been confirmed (in progress, or waiting for
 * the exporter's retry); `waiting` rows haven't been claimed yet.
 */
export const LakeEntityStatus = z.object({
    entityType: LakeEntityType,
    events: z.number().int(),
    exported: z.number().int(),
    claimed: z.number().int(),
    waiting: z.number().int(),
    oldestUnexportedAt: z.string().nullable(),
    lastExportedAt: z.string().nullable(),
    lastExportedPath: z.string().nullable(),
});
export const LakeExportStatus = z.object({ items: z.array(LakeEntityStatus) });
/** One exported (or claimed, not yet uploaded) JSON Lines file. */
export const LakeExportFile = z.object({
    batchId: z.string().uuid(),
    entityType: LakeEntityType,
    path: z.string(),
    rows: z.number().int(),
    claimedAt: z.string(),
    exportedAt: z.string().nullable(),
});
export const LakeFileList = z.object({ items: z.array(LakeExportFile) });
export const LakeFileQuery = z.object({
    entityType: LakeEntityType.optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
});
/** One lake exporter run. `error` is set when the run failed before exporting anything (e.g. storage unreachable). */
export const LakeExportRun = z.object({
    id: z.number().int(),
    startedAt: z.string(),
    finishedAt: z.string(),
    exported: z.array(z.object({ entityType: LakeEntityType, files: z.number().int(), rows: z.number().int() })),
    failed: z.array(z.object({ entityType: LakeEntityType, error: z.string() })),
    error: z.string().nullable(),
});
export const LakeRunList = z.object({ items: z.array(LakeExportRun) });
export const LakeRunQuery = z.object({ limit: z.coerce.number().int().min(1).max(200).default(20) });
//# sourceMappingURL=lake.js.map